import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { OrganizerForm } from './forms/OrganizerForm';
import { useOrganizersStore } from '../../stores/organizersStore';
import { createUserWithoutSignIn } from '../../lib/firebase/auth';
import { Organizer } from '../../types/organizer';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const AddOrganizerModal = ({ isOpen, onClose }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { createOrganizer } = useOrganizersStore();
  const { translations } = useLanguage();

  const handleSubmit = async (data: Partial<Organizer>) => {
    if (!data.email || !data.password || !data.name) {
      setError(
        translations?.addOrganizerModal?.requiredFieldsError || 
        'Name, email and password are required'
      );
      return;
    }
    
    setIsLoading(true);
    setError('');

    try {
      // Create the user in Firebase Auth
      const firebaseUser = await createUserWithoutSignIn(data.email, data.password);
      
      // Create the organizer profile
      const organizerData: Organizer = {
        id: firebaseUser.uid,
        name: data.name,
        email: data.email,
        password: data.password,
        country: data.country,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      await createOrganizer(organizerData);
      toast.success(
        translations?.addOrganizerModal?.successMessage || 
        'Organizer added successfully'
      );
      onClose();
    } catch (err: any) {
      console.error('Error creating organizer:', err);
      setError(
        err.message || 
        translations?.addOrganizerModal?.errorMessage || 
        'An error occurred while adding the organizer'
      );
      toast.error(
        translations?.addOrganizerModal?.errorToast || 
        'Failed to add the organizer'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={translations?.addOrganizerModal?.title || 'Add New Organizer'}
    >
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          {error}
        </div>
      )}
      <OrganizerForm
        onSubmit={handleSubmit}
        isLoading={isLoading}
      />
    </Modal>
  );
};